import React, { useState, useEffect, useCallback } from 'react';
import { z } from 'zod';
import { getSchemaDescription } from '../../lib/utils';
import { CodeEditor } from '../CodeEditor/CodeEditor';
import { PathInputWithAutocomplete } from './PathInputWithAutocomplete';
import { useSchemaMapperStore } from '../../store/schemaMapperStore';
import { execApi } from '../../api/execApi';
import { Save, Eye, EyeOff, PlusCircle, Trash2, AlertTriangle } from 'lucide-react';
import { ConfirmationModal } from '../ui/confirmation-modal';
import { LabelWithIcon } from '../ui/LabelWithIcon';
import { SchemaField } from '../../types/schemaMapperTypes';
import { FieldOutputPreview } from './FieldOutputPreview';
import { FieldNameEditor } from './FieldNameEditor';
import { AsyncOutputPreview } from './AsyncOutputPreview';
import JsonSerializer from '../../../../utilities/jsonSerializer';
import './SchemaMapper.css';

interface SchemaMapperProps {
  destinationZod: z.ZodTypeAny;
  sourceZod: z.ZodTypeAny;
  /** Serialized (JsonSerializer) string or already-deserialized mapping object. */
  schema: string | Record<string, unknown>;
  sampleData?: Record<string, unknown>;
  /** Persists the serialized schema; the page owns transport and cache updates. */
  onSave: (serializedSchema: string) => Promise<void>;
}

export const SchemaMapper: React.FC<SchemaMapperProps> = ({
  sourceZod,
  destinationZod,
  schema,
  sampleData: providedSampleData,
  onSave,
}) => {
  // Local state for UI elements
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [currentSchema, setCurrentSchema] = useState<Record<string, unknown>>({});
  const [sample, setSample] = useState<string>('');
  const [parsedSample, setParsedSample] = useState<Record<string, unknown>>({});
  const [showPreview, setShowPreview] = useState(false);
  // Add state to track which fields are being edited
  const [editingFieldIndices, setEditingFieldIndices] = useState<number[]>([]);
  // Add state for the field removal confirmation modal
  const [fieldToRemove, setFieldToRemove] = useState<number | null>(null);
  // Add state for save confirmation modal
  const [showSaveConfirmation, setShowSaveConfirmation] = useState<boolean>(false);

  // Use the Zustand store for core state and actions
  const {
    fields,
    hasValidationErrors,
    initializeFields,
    addField,
    removeField,
    updateField,
    setFieldValidation,
  } = useSchemaMapperStore();

  // Refs for component-internal logic (if needed in future)

  // Initialize sample data from provided sampleData (path-specific mock data)
  useEffect(() => {
    const initialSampleData = providedSampleData || {};
    setSample(JSON.stringify(initialSampleData, null, 2));
  }, [providedSampleData]);

  // Initialize schema from the prop
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    let loadedSchema: Record<string, unknown> = {};
    if (typeof schema === 'string') {
      // Use JsonSerializer for consistent deserialization
      try {
        loadedSchema = JsonSerializer.deserialize(schema) ?? {};
      } catch (error) {
        console.error('Error deserializing schema string:', error);
        setError(`Failed to parse schema: ${(error as Error).message}`);
      }
    } else if (schema && typeof schema === 'object') {
      // Use the schema object directly
      loadedSchema = schema as Record<string, unknown>;
    }

    setCurrentSchema(loadedSchema);
  }, [schema]);

  // Helper functions for field name editing
  const startEditingField = (index: number) => {
    setEditingFieldIndices(prev => [...prev, index]);
  };

  const cancelEditingField = (index: number) => {
    setEditingFieldIndices(prev => prev.filter(i => i !== index));
  };

  const saveFieldNameEdit = (index: number, newName: string) => {
    // Only update if name actually changed
    if (fields[index] && fields[index].name !== newName) {
      updateField(index, { name: newName });
    }
    setEditingFieldIndices(prev => prev.filter(i => i !== index));
  };

  // Simply updates the function value without any formatting
  const handleFunctionValueChange = (index: number, newValue: string) => {
    const field = fields[index];
    if (!field || field.type !== 'function') return;

    // Update the store with the value
    updateField(index, { value: newValue, functionValue: newValue });
  };

  // Render function for FieldOutputPreview, passing the store's validation action
  const renderFieldOutputPreview = useCallback((field: SchemaField) => {
    const previewProps = {
      field,
      sampleData: parsedSample,
      destinationZod,
    };

    return <FieldOutputPreview {...previewProps} />;
  }, [parsedSample, destinationZod]);

  // Parse the loaded schema and initialize the store fields
  useEffect(() => {
    try {
      initializeFields(currentSchema);
    } catch (e) {
      console.error("Error processing schema for initialization:", e);
      setError(`Error processing schema: ${(e as Error).message}`);
      initializeFields({});
    } finally {
      setIsLoading(false);
    }
  }, [currentSchema, initializeFields]);

  // Update sample data when changed
  useEffect(() => {
    if (!sample) return;
    try {
      setParsedSample(JSON.parse(sample));
      setError(null);
    } catch (e) {
      if (!error?.startsWith('Error initializing schema') && !error?.startsWith('Error processing schema')) {
         setError(`Invalid sample JSON: ${(e as Error).message}`);
      }
    }
  }, [sample, error]);

  // Add this near the top of the SchemaMapper component definition
  const evaluateOutput = async (): Promise<Record<string, unknown>> => {
    const output: Record<string, unknown> = {};
    const promises = [];

    // First process all path fields (synchronous)
    for (const field of fields) {
      if (!field.name) continue; // Skip fields without names

      if (field.type === 'path') {
        try {
          // Handle dot notation path
          const path = field.value.split('.');
          let value: unknown = { ...parsedSample };

          for (const segment of path) {
            if (
              value &&
              typeof value === 'object' &&
              segment in (value as Record<string, unknown>)
            ) {
              value = (value as Record<string, unknown>)[segment];
            } else {
              value = undefined;
              break;
            }
          }

          output[field.name] = value;
        } catch (e) {
          output[field.name] = `Error: ${(e as Error).message}`;
        }
      }
    }

    // Then process function fields (asynchronous)
    for (const field of fields) {
      if (!field.name || field.type !== 'function') continue; 

      // Use an immediately-invoked async function for each function field
      const processFunction = async () => {
        try {
          const functionCode = field.functionValue;
          
          // Use the execApi directly for consistent batch processing
          const response = await execApi.executeFunction({
            fn: functionCode,
            input: parsedSample
          });
          
          output[field.name] = response.result;
        } catch (error: unknown) {
          console.error('API execution failed:', error);
          const errorMessage = error instanceof Error 
            ? error.message 
            : (error as { message?: string })?.message || 'Unknown error';
          output[field.name] = `Error: ${errorMessage}`;
        }
      };
      
      promises.push(processFunction());
    }

    // Wait for all function executions to complete
    await Promise.all(promises);

    return output;
  };
  
  // Prepare schema and handle save
  const handleSaveClick = async () => {
    if (hasValidationErrors) {
      setError('Fix validation errors before saving');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const finalSchema: Record<string, unknown> = {};

      fields.forEach((field) => {
        if (field.name) {
          // For path fields, use pathValue; for function fields, use functionValue
          const fieldValue = field.type === 'path' ? field.pathValue : field.functionValue;
          finalSchema[field.name] = fieldValue;
        }
      });

      const serializedSchema = JsonSerializer.serialize(finalSchema);
      await onSave(serializedSchema);
      setCurrentSchema(finalSchema);
    } catch (error) {
      console.error('Error saving schema:', error);
      setError(`Error saving schema: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
      setShowSaveConfirmation(false);
    }
  };

  // Helper functions for field removal
  const openRemoveConfirmation = (index: number) => {
    setFieldToRemove(index);
  };

  const closeRemoveConfirmation = () => {
    setFieldToRemove(null);
  };

  const handleConfirmRemove = () => {
    if (fieldToRemove !== null) {
      removeField(fieldToRemove);
      setFieldToRemove(null);
    }
  };

  // Helper functions for save confirmation
  const openSaveConfirmation = () => {
    if (hasValidationErrors) {
      setError('Fix validation errors before saving');
      return;
    }

    setShowSaveConfirmation(true);
  };

  const closeSaveConfirmation = () => {
    setShowSaveConfirmation(false);
  };

  return (
    <div className="card">
      <div className="component-header">
        <h2>Schema Mapper</h2>
        <div className="component-controls">
          <button
            onClick={openSaveConfirmation}
            disabled={isLoading || isSaving || hasValidationErrors}
            className={`button-primary ${(isLoading || hasValidationErrors) ? 'disabled' : ''}`}
            title={isLoading ? "Loading schema..." : hasValidationErrors ? "Fix validation errors before saving" : "Save Schema"}
          >
            {isSaving ? 'Saving...' : <><Save size={16} /> Save Schema</>}
          </button>
          <button
            className="button-secondary"
            onClick={() => setShowPreview(!showPreview)}
            disabled={isLoading}
          >
            {showPreview ? <><EyeOff size={16} /> Hide Final Output</> : <><Eye size={16} /> Show Final Output</>}
          </button>
        </div>
      </div>

      {error && !error.startsWith('Invalid sample JSON') && <div className="schema-mapper-error">{error}</div>}

      {isLoading && <div className="schema-mapper-loading">Loading schema...</div>}

      {!isLoading && (
        <>
          {hasValidationErrors && (
            <div className="schema-mapper-validation-warning">
              <LabelWithIcon 
                icon={<AlertTriangle />} 
                text="There are validation errors in your field mappings. Please fix them before saving." 
                className="validation-warning-message"
                iconSize={18}
              />
            </div>
          )}

          <div className="schema-mapper-sample">
            {error && error.startsWith('Invalid sample JSON') && <div className="schema-mapper-error json-error">{error}</div>}
            <h3>Sample Input</h3>
            <div className="schema-mapper-code">
              <CodeEditor
                value={sample} 
                height="200px"
                onChange={(value: string) => setSample(value)}
                readOnly={false}
              />
            </div>
          </div>

          {showPreview && (
            <div className="schema-mapper-output">
              <h3>Final Output Preview</h3>
              <div className="schema-mapper-code">
                <AsyncOutputPreview evaluateOutput={evaluateOutput} />
              </div>
            </div>
          )}

          <div className="schema-mapper-fields">
            {fields.map((field, index) => {
              const fieldKey = field.name 
                ? `field-${field.name}-${index}`
                : `field-${field.lastUpdated || Date.now()}-${index}`;
                
              const isEditingName = editingFieldIndices.includes(index);
              const fieldDescription = field.name && destinationZod ? getSchemaDescription(destinationZod, field.name) : undefined;
                
              return (
                <div className="schema-mapper-field" key={fieldKey}>
                  <div className="field-header">
                    <div className="field-control">
                      <div className="field-label">Field Name</div>
                      <div className="field-input-container field-name-container">
                        <FieldNameEditor
                          index={index}
                          field={field}
                          isEditing={isEditingName}
                          onStartEditing={() => startEditingField(index)}
                          onCancelEditing={() => cancelEditingField(index)}
                          onSaveEdit={(newName) => saveFieldNameEdit(index, newName)}
                          description={fieldDescription}
                          destinationZod={destinationZod}
                        />
                      </div>
                    </div>
                    
                    <div className="field-control">
                      <div className="field-label">Type</div>
                      <div className="field-input-container field-type-container">
                        <div className="select-wrapper">
                          <select
                            id={`field-type-${index}`}
                            value={field.type}
                            onChange={(e) =>
                              updateField(index, {
                                type: e.target.value as 'path' | 'function',
                              })
                            }
                          >
                            <option value="path">Path</option>
                            <option value="function">Function</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="field-control field-action-control">
                      <div className="field-label empty-label">&nbsp;</div>
                      <div className="field-input-container field-action-container">
                        <button
                          className="button-danger button-icon button-full button-full-height"
                          onClick={() => openRemoveConfirmation(index)}
                          title="Remove field"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="field-value">
                    {field.type === 'path' ? (
                      <>
                        <PathInputWithAutocomplete
                          value={field.pathValue}
                          onChange={(newValue) => {
                            // Update both value and pathValue to ensure consistency
                            updateField(index, { value: newValue, pathValue: newValue });
                            
                            // Set field validation status - this ensures path changes are tracked
                            if (field.name) {
                              setFieldValidation(field.name, true);
                            }
                          }}
                          sourceZod={sourceZod}
                        />
                        <div className="field-section-divider">
                          <span>Field Output</span>
                        </div>
                        {renderFieldOutputPreview(field)}
                      </>
                    ) : (
                      <>
                        <div className="function-editor">
                          <label htmlFor={`field-func-${index}`}>Function</label>
                          <div className="schema-mapper-code">
                            <CodeEditor
                              id={`field-func-${index}`}
                              value={field.value}
                              height="150px"
                              onChange={(value: string) => handleFunctionValueChange(index, value)}
                              showFormatButton={true}
                              onFormat={(formattedValue: string) => {
                                // Update both value and functionValue on format
                                updateField(index, { 
                                  value: formattedValue, 
                                  functionValue: formattedValue 
                                });
                                if (field.name) {
                                  setFieldValidation(field.name, true);
                                }
                              }}
                            />
                          </div>
                        </div>
                        <div className="field-section-divider">
                          <span>Field Output</span>
                        </div>
                        {renderFieldOutputPreview(field)}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            
            <button
              className="button-primary button-full"
              onClick={addField}
              disabled={isLoading}
            >
              <PlusCircle size={18} /> Add Field
            </button>
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={fieldToRemove !== null}
        onClose={closeRemoveConfirmation}
        onConfirm={handleConfirmRemove}
        title="Remove Field"
        message={fieldToRemove !== null && fields[fieldToRemove]?.name 
          ? `Are you sure you want to remove the field "${fields[fieldToRemove].name}"?` 
          : "Are you sure you want to remove this field?"}
        warningMessage="This action cannot be undone."
        confirmLabel="Remove"
        type="danger"
      />

      <ConfirmationModal
        isOpen={showSaveConfirmation}
        onClose={closeSaveConfirmation}
        onConfirm={handleSaveClick}
        title="Save Schema"
        message="Are you sure you want to save this schema configuration?"
        confirmLabel="Save"
        type="info"
      />
    </div>
  );
}; 